import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box
} from '@mui/material';
import { Warning, Close } from '@mui/icons-material';
import { TextField } from '@mui/material';
import { colors, typography } from '../../theme/designSystem';
import SavingOverlay from '../SavingOverlay';

export interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  warningMessage?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  type?: 'danger' | 'warning' | 'info';
  // Quando true, bloqueia o modal com overlay "Processando" e desabilita os botões.
  isProcessing?: boolean;
  // Campo de justificativa (ex.: motivo da exclusão — RN-112h). Quando presente, o
  // botão de confirmar só habilita com o mínimo de caracteres preenchido.
  reasonLabel?: string;
  reasonValue?: string;
  onReasonChange?: (value: string) => void;
  reasonMinLength?: number;
  reasonMaxLength?: number;
  // false = justificativa OPCIONAL (o botão confirma mesmo vazia) — ex.: inativação de diagnóstico
  reasonRequired?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  warningMessage,
  confirmButtonText = 'Confirmar',
  cancelButtonText = 'Cancelar',
  onConfirm,
  onCancel,
  type = 'danger',
  isProcessing = false,
  reasonLabel,
  reasonValue = '',
  onReasonChange,
  reasonMinLength = 3,
  reasonMaxLength = 500,
  reasonRequired = true
}) => {
  const reasonInvalid = Boolean(reasonLabel) && reasonRequired && reasonValue.trim().length < reasonMinLength;
  const getConfirmButtonColor = () => {
    switch (type) {
      case 'danger':
        return '#dc3545';
      case 'warning':
        return '#ffc107';
      case 'info':
        return colors.primary;
      default:
        return '#dc3545';
    }
  };

  const getConfirmButtonHoverColor = () => {
    switch (type) {
      case 'danger':
        return '#c82333';
      case 'warning':
        return '#e0a800';
      case 'info':
        return '#029AAB';
      default:
        return '#c82333';
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isProcessing ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          position: 'relative',
          overflow: 'hidden',
        }
      }}
    >
      <SavingOverlay open={isProcessing} />
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: colors.white,
          padding: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontSize: '1.4rem',
            fontWeight: typography.fontWeight.semibold,
            margin: 0,
          }}
        >
          {title}
        </Typography>
        <IconButton
          onClick={onCancel}
          disabled={isProcessing}
          sx={{
            color: colors.white,
            padding: '0.25rem',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '2rem !important' }}>
        <Typography
          sx={{
            fontSize: '1rem',
            color: colors.text,
            marginBottom: warningMessage ? '1.5rem' : 0,
            lineHeight: 1.5,
          }}
        >
          {message}
        </Typography>

        {warningMessage && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.875rem 1rem',
              backgroundColor: '#fff3cd',
              color: '#856404',
              borderRadius: '6px',
              border: '1px solid #ffeaa7',
            }}
          >
            <Warning sx={{ fontSize: '1.25rem', color: '#f59e0b' }} />
            <Typography sx={{ fontSize: '0.875rem', color: '#856404', lineHeight: 1.5 }}>
              {warningMessage}
            </Typography>
          </Box>
        )}

        {reasonLabel && (
          <TextField
            label={reasonLabel}
            value={reasonValue}
            onChange={(e) => onReasonChange?.(e.target.value)}
            required
            fullWidth
            multiline
            minRows={2}
            size="small"
            disabled={isProcessing}
            inputProps={{ maxLength: reasonMaxLength }}
            InputLabelProps={{ shrink: true }}
            helperText={`${reasonValue.length}/${reasonMaxLength}`}
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.white,
                '& fieldset': { borderColor: colors.border, legend: { maxWidth: '100%' } },
                '&.Mui-focused fieldset': {
                  borderColor: colors.primary,
                  boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem',
                color: colors.textSecondary,
                backgroundColor: colors.white,
                paddingLeft: '4px',
                paddingRight: '4px',
                '&.Mui-focused': { color: colors.primary },
              },
            }}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: '1.5rem 2rem',
          borderTop: `1px solid ${colors.backgroundAlt}`,
          backgroundColor: colors.background,
          gap: '1rem',
        }}
      >
        <Button
          onClick={onCancel}
          disabled={isProcessing}
          variant="outlined"
          sx={{
            padding: '0.75rem 1.5rem',
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            backgroundColor: colors.white,
            color: colors.textSecondary,
            fontSize: '1rem',
            fontWeight: typography.fontWeight.medium,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: colors.background,
              borderColor: '#adb5bd',
            }
          }}
        >
          {cancelButtonText}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isProcessing || reasonInvalid}
          variant="contained"
          sx={{
            padding: '0.75rem 1.5rem',
            borderRadius: '6px',
            backgroundColor: getConfirmButtonColor(),
            color: colors.white,
            fontSize: '1rem',
            fontWeight: typography.fontWeight.medium,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: getConfirmButtonHoverColor(),
              boxShadow: 'none',
              transform: 'translateY(-1px)',
            }
          }}
        >
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmModal;
